import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, Card, Col, Modal, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import moment from "moment";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import SkinAssistantNav from "../../components/admin/SkinAssistantNav";
import AdminEmptyState from "../../components/admin/AdminEmptyState";
import DataTableSkeleton from "../../components/DataTableSkeleton";
import { dataTableCustomStyles, dataTablePaginationOptions } from "../../components/admin/dataTableConfig";
import {
  API_NOT_DEPLOYED_MSG,
  SKIN_ASSISTANT_TURNS_PREVIEW_LIMIT,
  formatChatParticipant,
  formatChatParticipantSubline,
  getSkinAssistantChats,
  isSkinAssistantApiNotDeployed,
  getSessionTurnCount,
  getSessionTurns,
  mergeSessionAfterRefetch,
  parseChatsResponse,
  sessionShowsLastTenTurnsNote,
} from "../../helper/skin_assistant_helper";
import { getAdminToken } from "../../helper/auth_helper";

function roleBadgeVariant(role) {
  return role === "assistant" ? "success" : "primary";
}

function ChatsList() {
  const token = getAdminToken();

  const [loading, setLoading] = useState(true);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [detailError, setDetailError] = useState("");

  const fetchSessions = useCallback(async () => {
    if (!token) {
      setLoadError("Not authorized");
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError("");
    setApiUnavailable(false);
    try {
      const res = await getSkinAssistantChats({ page, limit: perPage }, token);
      const parsed = parseChatsResponse(res);
      if (parsed.error) {
        setLoadError(parsed.error);
        setSessions([]);
        return;
      }
      setSessions(parsed.sessions);
      setPagination(parsed.pagination);
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setApiUnavailable(true);
        setSessions([]);
      } else {
        setLoadError(error?.message || "Failed to load chats");
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const openDetail = useCallback(async (row) => {
    setSelectedSession(row);
    setDetailOpen(true);
    setDetailError("");

    if (!row?.sessionId) return;

    setDetailLoading(true);
    try {
      const res = await getSkinAssistantChats(
        { sessionId: row.sessionId, limit: 1 },
        token
      );
      const parsed = parseChatsResponse(res);
      if (parsed.error) {
        setDetailError(parsed.error);
        return;
      }
      if (parsed.sessions[0]) {
        setSelectedSession(mergeSessionAfterRefetch(row, parsed.sessions[0]));
      }
    } catch (error) {
      if (isSkinAssistantApiNotDeployed(error)) {
        setDetailError(API_NOT_DEPLOYED_MSG);
      } else {
        setDetailError(error?.message || "Could not refresh session");
      }
    } finally {
      setDetailLoading(false);
    }
  }, [token]);

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedSession(null);
    setDetailError("");
  };

  const columns = useMemo(
    () => [
      {
        name: "Started",
        selector: (row) => row.createdAt,
        sortable: true,
        width: "165px",
        cell: (row) =>
          row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A") : "—",
      },
      {
        name: "Participant",
        grow: 1,
        cell: (row) => (
          <div>
            <div className="fw-semibold small">{formatChatParticipant(row)}</div>
            <div className="text-secondary" style={{ fontSize: "0.75rem" }}>
              {row.isGuest ? "Guest session" : "Registered user"}
            </div>
          </div>
        ),
      },
      {
        name: "Turns",
        selector: (row) => getSessionTurnCount(row),
        width: "80px",
        cell: (row) => getSessionTurnCount(row),
      },
      {
        name: "Blocked",
        selector: (row) => row.blockedCount,
        width: "85px",
      },
      {
        name: "Tokens",
        selector: (row) => row.usageTotals?.totalTokens,
        width: "90px",
        cell: (row) => row.usageTotals?.totalTokens ?? 0,
      },
      {
        name: "Last activity",
        width: "165px",
        cell: (row) =>
          row.updatedAt ? moment(row.updatedAt).format("DD MMM YYYY, hh:mm A") : "—",
      },
      {
        name: "",
        width: "90px",
        cell: (row) => (
          <Button variant="outline-primary" size="sm" onClick={() => openDetail(row)}>
            View
          </Button>
        ),
      },
    ],
    [openDetail]
  );

  const totalCount = pagination?.total ?? sessions.length;
  const turns = getSessionTurns(selectedSession);
  const selectedTurnCount = getSessionTurnCount(selectedSession);
  const showTenTurnsNote = sessionShowsLastTenTurnsNote(selectedSession);

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-md-flex align-items-center justify-content-between mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard">Dashboard</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Skin Assistant
              </li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">Skin Assistant — Chats</h4>
            <p className="text-secondary small mb-0 mt-1">
              Chat sessions with redacted turn content (API returns last{" "}
              {SKIN_ASSISTANT_TURNS_PREVIEW_LIMIT} turns per session)
            </p>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={fetchSessions} disabled={loading}>
            Refresh
          </Button>
        </div>

        <SkinAssistantNav />

        {apiUnavailable && (
          <Alert variant="warning" className="mb-3">{API_NOT_DEPLOYED_MSG}</Alert>
        )}
        {loadError && !apiUnavailable && (
          <Alert variant="danger" className="mb-3">{loadError}</Alert>
        )}

        <Card>
          <Card.Body>
            {loading ? (
              <DataTableSkeleton rows={6} />
            ) : apiUnavailable ? (
              <AdminEmptyState
                icon="ri-cloud-off-line"
                title="Chats API not available"
                description={API_NOT_DEPLOYED_MSG}
              />
            ) : sessions.length === 0 ? (
              <AdminEmptyState
                title="No chat sessions yet"
                description="Sessions appear when shoppers use the Skin Assistant chat on the storefront."
              />
            ) : (
              <DataTable
                columns={columns}
                data={sessions}
                customStyles={dataTableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalCount}
                paginationPerPage={perPage}
                paginationDefaultPage={page}
                onChangePage={setPage}
                onChangeRowsPerPage={(newPerPage, newPage) => {
                  setPerPage(newPerPage);
                  setPage(newPage);
                }}
                paginationComponentOptions={dataTablePaginationOptions}
                highlightOnHover
                responsive
              />
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={detailOpen} onHide={closeDetail} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Chat session</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSession && (
            <Row className="g-2 mb-3 small">
              <Col md={6}>
                <strong>Participant:</strong> {formatChatParticipant(selectedSession)}
              </Col>
              <Col md={6}>
                <strong>Turns:</strong> {selectedTurnCount}
              </Col>
              <Col md={12} className="text-secondary">
                {formatChatParticipantSubline(selectedSession)}
              </Col>
              <Col md={4}>
                <strong>Blocked:</strong> {selectedSession.blockedCount ?? 0}
              </Col>
              <Col md={4}>
                <strong>Total tokens:</strong> {selectedSession.usageTotals?.totalTokens ?? 0}
              </Col>
              <Col md={4}>
                <strong>Model:</strong> {selectedSession.lastModel || "—"}
              </Col>
              <Col md={6}>
                <strong>Started:</strong>{" "}
                {selectedSession.createdAt
                  ? moment(selectedSession.createdAt).format("DD MMM YYYY, hh:mm A")
                  : "—"}
              </Col>
            </Row>
          )}

          {detailLoading && (
            <p className="text-secondary small">Refreshing session…</p>
          )}
          {detailError && (
            <Alert variant="warning" className="small">{detailError}</Alert>
          )}

          {showTenTurnsNote && (
            <Alert variant="info" className="small py-2">
              Showing last {SKIN_ASSISTANT_TURNS_PREVIEW_LIMIT} turns
              {selectedSession?.turnCount
                ? ` (${selectedSession.turnCount} total in session).`
                : "."}
            </Alert>
          )}

          {!detailLoading && turns.length === 0 && !detailError && selectedTurnCount === 0 && (
            <AdminEmptyState
              title="No turns"
              description="This session has no redacted turns in the API payload."
            />
          )}
          {!detailLoading && turns.length === 0 && !detailError && selectedTurnCount > 0 && (
            <Alert variant="secondary" className="small">
              This session has {selectedTurnCount} turn(s), but no `turns[]` payload was returned.
              Try refreshing the list or reopen after the API includes redacted turns.
            </Alert>
          )}

          <div className="skin-assistant-chat-transcript d-flex flex-column gap-2">
            {turns.map((turn) => (
              <div
                key={turn.id}
                className="border rounded p-2"
                style={{ background: turn.role === "assistant" ? "#f0fdf4" : "#f8fafc" }}
              >
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <Badge bg={roleBadgeVariant(turn.role)} className="text-capitalize">
                    {turn.role}
                  </Badge>
                  <span className="text-secondary" style={{ fontSize: "0.75rem" }}>
                    {turn.charCount} chars
                  </span>
                </div>
                <p className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
                  {turn.contentRedacted || "—"}
                </p>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeDetail}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Footer />
    </>
  );
}

export default ChatsList;
